/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue May 28 2024
 **/

import { isValidElement } from "react";
import { has, isEmpty } from "lodash-es";
import { _T, _P } from "@rfice/language";

import { ModalPopup, Typography, useModal } from "@/frameworks/designStuff/components";
import type { IconName } from "frameworks/designStuff/components";
import { ModalID } from "@/frameworks/core/commons";

interface ShowConfirmOptions {
	size?: 440 | 600;
	onConfirm?: () => void;
	onCancel?: () => void;
	preventHideByDimmed?: boolean;
}

type Text = string | ReturnType<typeof _T> | ReturnType<typeof _P>;

type Contents = {
	title?: Text;
	descriptions?: Text;
	descriptionImg?: string;
	confirmText?: string;
	cancelText?: string;
	useOnlyConfirm?: boolean;
	imgWidth?: number;
	imgHeight?: number;
	descriptionWidth?: string;
	titleIcon?: IconName;
	descriptionAlign?: "center" | "left" | "right";
};

function isContents(contents: Text | Contents): contents is Contents {
	if (isValidElement(contents)) return false;
	if (isEmpty(contents)) return true;
	return (
		has(contents, "title") ||
		has(contents, "descriptions") ||
		has(contents, "confirmText") ||
		has(contents, "cancelText")
	);
}

export default function useConfirm() {
	const modal = useModal();

	function confirm(contents: Contents, options?: ShowConfirmOptions): void;
	function confirm(title: Text, options?: ShowConfirmOptions): void;
	function confirm(contents: Text | Contents, options?: ShowConfirmOptions) {
		const submitConfirm = (_: unknown, hide: () => void) => {
			options?.onConfirm?.();
			hide();
		};

		const confirmText = isContents(contents) ? contents.confirmText : _T("확인");
		const cancelText = isContents(contents) ? contents.cancelText : _T("취소");
		const title = isContents(contents) ? contents.title : contents;
		const titleIcon = isContents(contents) ? contents.titleIcon : undefined;
		const description = isContents(contents) ? contents.descriptions : undefined;
		const descriptionImg = isContents(contents) ? contents.descriptionImg : false;
		const imgWidth = isContents(contents) ? contents.imgWidth : undefined;
		const imgHeight = isContents(contents) ? contents.imgHeight : undefined;
		const descriptionWidth = isContents(contents) ? contents.descriptionWidth : undefined;
		const isOnlyConfirm = isContents(contents) ? contents.useOnlyConfirm : false;
		const modalID = title ? `${ModalID.CONFIRM_POPUP}_${title}` : ModalID.CONFIRM_POPUP;
		const descriptionAlign = isContents(contents) ? contents.descriptionAlign : "left";

		modal.show({
			id: modalID,
			content: (
				<ModalPopup
					title={title}
					titleTypography={{
						typoSize: "Title1",
						align: "left"
					}}
					fitContent
					titleIcon={titleIcon}
					modalID={modalID}
					confirmText={confirmText ?? _T("확인")}
					cancelText={cancelText ?? (isOnlyConfirm ? undefined : _T("취소"))}
					onConfirm={submitConfirm}
					onCancel={options?.onCancel}
					width={options?.size ?? 440}
				>
					{description && (
						<Typography
							style={{ width: descriptionWidth }}
							tag={typeof description === "string" ? "span" : "div"}
							typoSize="Body2"
							typoColor="neutral_70_100"
							align={descriptionAlign}
							maxLine={3}
							ellipsis
						>
							{description}
						</Typography>
					)}
					{descriptionImg && <img src={descriptionImg} style={{ width: imgWidth, height: imgHeight }} />}
				</ModalPopup>
			),
			preventHideByDimmed: options?.preventHideByDimmed ?? false
		});
	}

	return { confirm };
}
